import { useCallback, useEffect, useMemo, useState } from 'react';
import { normalizeScenario } from '../domain/equityEngine';
import type { Scenario } from '../domain/types';
import { STORAGE_SCHEMA_VERSION } from '../domain/types';

const STORAGE_KEY = 'agreement-equity-playground:scenario:v2';

interface StoredScenario {
  schemaVersion: number;
  scenario: Scenario;
}

export function usePersistentScenario(defaultScenario: Scenario) {
  const normalizedDefault = useMemo(() => normalizeScenario(defaultScenario), [defaultScenario]);

  const [scenario, setScenarioState] = useState<Scenario>(() => {
    if (typeof window === 'undefined') {
      return normalizedDefault;
    }

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return normalizedDefault;
      }

      const parsed = JSON.parse(raw) as StoredScenario;
      if (parsed.schemaVersion !== STORAGE_SCHEMA_VERSION || !parsed.scenario) {
        return normalizedDefault;
      }

      return normalizeScenario(parsed.scenario);
    } catch {
      return normalizedDefault;
    }
  });

  useEffect(() => {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        schemaVersion: STORAGE_SCHEMA_VERSION,
        scenario,
      } satisfies StoredScenario),
    );
  }, [scenario]);

  const setScenario = useCallback((updater: Scenario | ((current: Scenario) => Scenario)) => {
    setScenarioState((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      return normalizeScenario(next);
    });
  }, []);

  const resetScenario = useCallback(() => {
    setScenarioState(normalizedDefault);
    window.localStorage.removeItem(STORAGE_KEY);
  }, [normalizedDefault]);

  return {
    scenario,
    setScenario,
    resetScenario,
  };
}
